import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useAccounts } from "./useAccounts";
import { toast } from "sonner";
import { getErrorMessage } from "@/lib/errors";
import type { AnyValue } from "@/lib/rowTypes";

export type VehicleScreen =
  | 'welcome' | 'marketplace' | 'detail' | 'needs_check'
  | 'down_payment' | 'financing' | 'hidden_costs' | 'affordability'
  | 'negotiation' | 'repair_event' | 'final_summary' | 'completed';

export interface FictionalVehicle {
  id: string;
  vehicle_key: string;
  vehicle_name: string;
  vehicle_type: string;
  description: string;
  purchase_price: number;
  monthly_payment_low: number;
  monthly_payment_high: number;
  insurance_monthly: number;
  maintenance_monthly: number;
  fuel_monthly: number;
  practicality_rating: number;
  status_rating: number;
  efficiency_rating: number;
  reliability_rating: number;
  tags: string[];
  ideal_buyer: string;
  pros: string;
  cons: string;
  display_order: number;
}

export interface VehicleFinancialProfile {
  checking: number;
  savings: number;
  emergencyFund: number;
  monthlyIncome: number;
  monthlyExpenses: number;
  creditScore: number;
  existingDebt: number;
}

export const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

const DEFAULT_PROFILE: VehicleFinancialProfile = {
  checking: 18000, savings: 30000, emergencyFund: 12000,
  monthlyIncome: 5500, monthlyExpenses: 2300, creditScore: 700, existingDebt: 0,
};

const SCREEN_PROGRESS: Record<VehicleScreen, number> = {
  welcome: 4, marketplace: 12, detail: 20, needs_check: 28,
  down_payment: 38, financing: 48, hidden_costs: 58, affordability: 68,
  negotiation: 76, repair_event: 86, final_summary: 94, completed: 100,
};

export const PURCHASE_REASONS = [
  { key: 'commute', label: 'Commuting reliably', icon: '🚗' },
  { key: 'family', label: 'Transporting family', icon: '👨‍👩‍👧‍👦' },
  { key: 'work', label: 'Supporting work or side income', icon: '🔧' },
  { key: 'replace', label: 'Replacing a broken vehicle', icon: '🔄' },
  { key: 'upgrade', label: 'Upgrading lifestyle', icon: '⬆️' },
  { key: 'status', label: 'Wanting a reward/status purchase', icon: '✨' },
];

export function getAPR(creditScore: number): Record<number, number> {
  if (creditScore >= 750) return { 36: 0.049, 48: 0.052, 60: 0.055, 72: 0.059 };
  if (creditScore >= 700) return { 36: 0.059, 48: 0.063, 60: 0.069, 72: 0.075 };
  if (creditScore >= 650) return { 36: 0.079, 48: 0.085, 60: 0.092, 72: 0.099 };
  return { 36: 0.109, 48: 0.119, 60: 0.129, 72: 0.139 };
}

export function calcMonthlyPayment(loan: number, apr: number, months: number) {
  const r = apr / 12;
  if (r === 0) return Math.round(loan / months);
  return Math.round(loan * (r * Math.pow(1 + r, months)) / (Math.pow(1 + r, months) - 1));
}

export function calcHiddenCosts(price: number) {
  return {
    salesTax: Math.round(price * 0.07),
    registration: 350,
    dealerFee: 500,
    titleFee: 75,
    total: Math.round(price * 0.07) + 350 + 500 + 75,
  };
}

export function getAffordabilityRating(
  monthlyRemaining: number,
  liquidAfter: number,
  emergencyFund: number,
  dti: number
): 'strong' | 'reasonable' | 'overextended' | 'unhealthy' {
  if (monthlyRemaining >= 800 && liquidAfter >= 8000 && emergencyFund > 3000 && dti < 0.36) return 'strong';
  if (monthlyRemaining >= 300 && liquidAfter >= 3000 && dti < 0.45) return 'reasonable';
  if (monthlyRemaining >= 0 && liquidAfter >= 0) return 'overextended';
  return 'unhealthy';
}

export function getPurchaseGrade(rating: string, reasonMatch: boolean): string {
  if (rating === 'strong' && reasonMatch) return 'A';
  if (rating === 'strong') return 'A-';
  if (rating === 'reasonable' && reasonMatch) return 'B+';
  if (rating === 'reasonable') return 'B';
  if (rating === 'overextended' && reasonMatch) return 'C+';
  if (rating === 'overextended') return 'C';
  return 'D';
}

export function checkReasonMismatch(reason: string, vehicleKey: string): string | null {
  if ((reason === 'commute' || reason === 'replace') && vehicleKey === 'luxury')
    return "You said you need practical transportation, but you chose the most expensive, least practical vehicle.";
  if (reason === 'family' && (vehicleKey === 'luxury' || vehicleKey === 'starter'))
    return "For family use, consider a vehicle with more space and reliability.";
  if (reason === 'work' && vehicleKey === 'luxury')
    return "A luxury performance car may not be the best fit for work-related transportation needs.";
  if (reason === 'status' && vehicleKey === 'starter')
    return "You wanted a status vehicle but chose the most budget-friendly option — that's actually smart!";
  return null;
}

/* ============================================================ */
/* Decision log + repair-event types (local-only sim state,     */
/* persisted into `negotiation_result` JSONB at run end).        */
/* ============================================================ */

export type DecisionKey =
  | 'reason' | 'vehicle' | 'down_payment' | 'loan_term'
  | 'negotiation' | 'repair' | 'final';

export interface DecisionEntry {
  key: DecisionKey;
  label: string;
  choice: string;
  consequence: string;
  lumiSignal: string;
  walletImpact: number;   // negative = cost, positive = saved/gained
  scoreDelta: number;     // -10..+15 judgment score
}

export type RepairChoice = 'pay_cash' | 'use_credit' | 'postpone';

export interface RepairEvent {
  amount: number;
  description: string;
  choice: RepairChoice | null;
}

/* Deterministic repair event sized to the vehicle so the same         */
/* learner sees the same surprise within one run, but different cars   */
/* generate different repairs.                                         */
function buildRepairEvent(vehicle: FictionalVehicle | null): RepairEvent {
  if (!vehicle) return { amount: 0, description: '', choice: null };
  // Lower reliability rating → bigger surprise.
  const reliability = Math.max(1, vehicle.reliability_rating || 3);
  const base = Math.round((vehicle.purchase_price * 0.025) / reliability * 5);
  const amount = Math.max(450, Math.min(3200, base));
  const candidates = [
    'Six-month check-in: the transmission throws a warning code at the dealer.',
    'After 4 months, the brakes need new pads, rotors, and a fluid flush.',
    'A pothole shreds a tire and bends a rim during the first winter.',
    'The AC compressor fails the first hot weekend of the year.',
    'A 30,000-mile service plus an unexpected sensor replacement comes due.',
  ];
  const idx = (vehicle.vehicle_name.length + vehicle.purchase_price) % candidates.length;
  return { amount, description: candidates[idx], choice: null };
}

/* Readiness labels for the end-of-run recap. */
export type ReadinessLabel =
  | 'Cashflow Strategist'
  | 'Smart Protector'
  | 'Risk Watcher'
  | 'Status Chaser'
  | 'Overextended Buyer'
  | 'Patient Walker';

export function useVehicleSim() {
  const { user } = useAuth();
  const { accounts, refetch: refetchAccounts } = useAccounts();

  const [simId, setSimId] = useState<string | null>(null);
  const [screen, setScreenRaw] = useState<VehicleScreen>('welcome');
  const [vehicles, setVehicles] = useState<FictionalVehicle[]>([]);
  const [profile, setProfile] = useState<VehicleFinancialProfile>(DEFAULT_PROFILE);
  const [scenarioType, setScenarioType] = useState<'live' | 'default'>('default');
  const [selectedVehicleKey, setSelectedVehicleKey] = useState<string | null>(null);
  const [purchaseReason, setPurchaseReason] = useState<string | null>(null);
  const [downPaymentPct, setDownPaymentPct] = useState(0.1);
  const [loanTermMonths, setLoanTermMonths] = useState(60);
  const [negotiationDiscount, setNegotiationDiscount] = useState(0);
  const [purchaseCompleted, setPurchaseCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [popupKey, setPopupKey] = useState<string | null>(null);

  /* New: decision log, no-redo flags, repair event. Local-only until */
  /* the run ends, then persisted into negotiation_result JSONB.       */
  const [decisionLog, setDecisionLog] = useState<DecisionEntry[]>([]);
  const [committedSteps, setCommittedSteps] = useState<Record<string, boolean>>({});
  const [repair, setRepair] = useState<RepairEvent>({ amount: 0, description: '', choice: null });

  const ref = useRef({
    selectedVehicleKey, downPaymentPct, loanTermMonths, profile, scenarioType,
    purchaseReason, negotiationDiscount, decisionLog, repair,
  });
  useEffect(() => {
    ref.current = {
      selectedVehicleKey, downPaymentPct, loanTermMonths, profile, scenarioType,
      purchaseReason, negotiationDiscount, decisionLog, repair,
    };
  });

  useEffect(() => {
    supabase.from("fictional_vehicles").select("*").order("display_order").then(({ data }) => {
      setVehicles((data) || []);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("vehicle_simulations")
        .select("*").eq("user_id", user.id)
        .order("created_at", { ascending: false }).limit(1);
      if (data && (data).length > 0) {
        const s = (data)[0];
        setSimId(s.id);
        setScreenRaw(s.current_screen as VehicleScreen);
        setScenarioType(s.scenario_type === 'live_user_finances' ? 'live' : 'default');
        if (s.financial_profile && Object.keys(s.financial_profile).length > 0) setProfile(s.financial_profile as AnyValue);
        if (s.selected_vehicle_key) setSelectedVehicleKey(s.selected_vehicle_key);
        if (s.purchase_reason) setPurchaseReason(s.purchase_reason);
        if (s.down_payment_percent != null) setDownPaymentPct(Number(s.down_payment_percent));
        if (s.loan_term_months) setLoanTermMonths(s.loan_term_months);
        if ((s as AnyValue).negotiation_result?.discount) setNegotiationDiscount((s as AnyValue).negotiation_result.discount);
        setPurchaseCompleted(!!s.purchase_completed);
      }
      setLoading(false);
    })();
  }, [user]);

  const selectedVehicle = vehicles.find(v => v.vehicle_key === selectedVehicleKey) || null;

  const effectivePrice = selectedVehicle ? selectedVehicle.purchase_price - negotiationDiscount : 0;
  const downPaymentAmount = Math.round(effectivePrice * downPaymentPct);
  const loanAmount = effectivePrice - downPaymentAmount;
  const aprMap = getAPR(profile.creditScore);
  const apr = aprMap[loanTermMonths] || 0.069;
  const monthlyPayment = loanAmount > 0 ? calcMonthlyPayment(loanAmount, apr, loanTermMonths) : 0;
  const totalPaid = monthlyPayment * loanTermMonths;
  const totalInterest = totalPaid - loanAmount;
  const hiddenCosts = selectedVehicle ? calcHiddenCosts(effectivePrice) : { salesTax: 0, registration: 0, dealerFee: 0, titleFee: 0, total: 0 };
  const upfrontCash = downPaymentAmount + hiddenCosts.total;
  const liquidBefore = profile.checking + profile.savings;
  const liquidAfter = liquidBefore - upfrontCash;
  const totalMonthlyCost = selectedVehicle ? monthlyPayment + selectedVehicle.insurance_monthly + selectedVehicle.maintenance_monthly + selectedVehicle.fuel_monthly : 0;
  const monthlyRemaining = profile.monthlyIncome - profile.monthlyExpenses - totalMonthlyCost - profile.existingDebt;
  const dti = profile.monthlyIncome > 0 ? (profile.monthlyExpenses + totalMonthlyCost + profile.existingDebt) / profile.monthlyIncome : 1;
  const affordabilityRating = selectedVehicle ? getAffordabilityRating(monthlyRemaining, liquidAfter, profile.emergencyFund, dti) : null;
  const reasonMismatch = purchaseReason && selectedVehicleKey ? checkReasonMismatch(purchaseReason, selectedVehicleKey) : null;
  const purchaseGrade = affordabilityRating ? getPurchaseGrade(affordabilityRating, !reasonMismatch) : null;
  const firstYearCost = upfrontCash + (totalMonthlyCost * 12);
  const progress = SCREEN_PROGRESS[screen] || 0;

  const persist = useCallback(async (screenOverride: VehicleScreen, extra?: Record<string, AnyValue>) => {
    if (!user) return;
    const r2 = ref.current;
    const payload: AnyValue = {
      current_screen: screenOverride,
      selected_vehicle_key: r2.selectedVehicleKey,
      purchase_reason: r2.purchaseReason,
      down_payment_percent: r2.downPaymentPct,
      loan_term_months: r2.loanTermMonths,
      credit_score: r2.profile.creditScore,
      financial_profile: r2.profile,
      scenario_type: r2.scenarioType === 'live' ? 'live_user_finances' : 'default_profile',
      negotiation_result: {
        discount: r2.negotiationDiscount,
        decision_log: r2.decisionLog,
        repair: r2.repair,
      },
      updated_at: new Date().toISOString(),
      ...extra,
    };
    if (simId) {
      await supabase.from("vehicle_simulations").update(payload).eq("id", simId);
    }
  }, [user, simId]);

  const goTo = useCallback((s: VehicleScreen) => {
    setScreenRaw(s);
    persist(s);
  }, [persist]);

  /* ----- Decision recorder ----- */
  const recordDecision = useCallback((entry: DecisionEntry) => {
    setDecisionLog((log) => {
      // De-dupe: replace any prior entry with the same key (handles
      // QA-only step replays without bloating the log).
      const without = log.filter((e) => e.key !== entry.key);
      return [...without, entry];
    });
    setCommittedSteps((c) => ({ ...c, [entry.key]: true }));
  }, []);

  /* Commit helpers per consequential step. Each one logs a               */
  /* Lumi signal and a wallet/score delta tied to the actual numbers.    */

  const commitVehicleChoice = useCallback(() => {
    if (!selectedVehicle || !purchaseReason) return;
    const mismatch = checkReasonMismatch(purchaseReason, selectedVehicleKey || '');
    const reason = PURCHASE_REASONS.find((r) => r.key === purchaseReason)?.label || purchaseReason;
    recordDecision({
      key: 'reason',
      label: 'Why this vehicle',
      choice: `${reason} → ${selectedVehicle.vehicle_name}`,
      consequence: mismatch
        ? `Mismatch flagged: ${mismatch}`
        : 'The vehicle fits the stated need.',
      lumiSignal: mismatch
        ? 'A vehicle that does not match your real need is a status purchase in disguise. Status decisions cost the most.'
        : 'Choosing transportation around a real need keeps the math grounded later.',
      walletImpact: 0,
      scoreDelta: mismatch ? -8 : 6,
    });
  }, [selectedVehicle, purchaseReason, selectedVehicleKey, recordDecision]);

  const commitDownPayment = useCallback(() => {
    if (!selectedVehicle) return;
    const cushionHit = profile.emergencyFund > 0
      ? Math.max(0, downPaymentAmount + hiddenCosts.total - (profile.checking - 1500))
      : 0;
    const aggressive = downPaymentPct >= 0.2;
    const minimal = downPaymentPct <= 0.05;
    recordDecision({
      key: 'down_payment',
      label: 'Down payment',
      choice: `${Math.round(downPaymentPct * 100)}% down (${fmt(downPaymentAmount)}) + ${fmt(hiddenCosts.total)} fees`,
      consequence: cushionHit > 0
        ? `Upfront cash pressures your cushion by ${fmt(cushionHit)}.`
        : aggressive
          ? 'Larger down payment cuts your loan and total interest.'
          : minimal
            ? 'Minimal down payment keeps cash on hand but maximizes the loan.'
            : 'Balanced down payment — moderate loan, moderate cushion.',
      lumiSignal: aggressive
        ? 'Putting more down is cheapest over time — as long as it does not wipe out your emergency cash.'
        : minimal
          ? 'A tiny down payment means you owe almost the full car. If you sell next year, you could owe more than it is worth.'
          : 'A 10–20% down payment is the usual sweet spot when you also need cash for fees and life.',
      walletImpact: -(downPaymentAmount + hiddenCosts.total),
      scoreDelta: aggressive ? 8 : minimal ? -4 : 4,
    });
  }, [selectedVehicle, downPaymentPct, downPaymentAmount, hiddenCosts.total, profile, recordDecision]);

  const commitLoanTerm = useCallback(() => {
    if (!selectedVehicle) return;
    const long = loanTermMonths >= 72;
    const short = loanTermMonths <= 36;
    recordDecision({
      key: 'loan_term',
      label: 'Loan term',
      choice: `${loanTermMonths} months @ ${(apr * 100).toFixed(1)}% APR`,
      consequence: `Total interest: ${fmt(totalInterest)} over the loan.`,
      lumiSignal: long
        ? 'A long loan lowers your monthly payment but quietly adds thousands in interest — and you stay underwater longer.'
        : short
          ? 'A short loan costs more each month but builds equity fastest and pays the least interest.'
          : 'A mid-length loan trades a manageable payment for moderate interest. Watch the total, not just the monthly.',
      walletImpact: -totalInterest,
      scoreDelta: long ? -6 : short ? 8 : 3,
    });
  }, [selectedVehicle, loanTermMonths, apr, totalInterest, recordDecision]);

  const commitNegotiation = useCallback(() => {
    if (!selectedVehicle) return;
    recordDecision({
      key: 'negotiation',
      label: 'Negotiation',
      choice: negotiationDiscount > 0 ? `Saved ${fmt(negotiationDiscount)}` : 'Took the listed price',
      consequence: negotiationDiscount > 0
        ? `Lower price means a smaller loan and less interest.`
        : 'Sticker price stands. Dealer keeps the margin.',
      lumiSignal: negotiationDiscount > 0
        ? 'A small price cut compounds: smaller loan, less interest, lower sales tax. Always ask.'
        : 'Walking in without asking leaves money on the table. The worst they say is no.',
      walletImpact: negotiationDiscount,
      scoreDelta: negotiationDiscount > 0 ? 5 : -2,
    });
  }, [selectedVehicle, negotiationDiscount, recordDecision]);

  const commitRepair = useCallback((choice: RepairChoice) => {
    const r = ref.current.repair;
    if (!r.amount) return;
    setRepair((cur) => ({ ...cur, choice }));
    const consequenceByChoice: Record<RepairChoice, { consequence: string; signal: string; impact: number; score: number; }> = {
      pay_cash: {
        consequence: `Repair paid in cash: ${fmt(r.amount)} out of savings.`,
        signal: 'Cash repairs sting once and end. That is what an emergency fund is for.',
        impact: -r.amount,
        score: 6,
      },
      use_credit: {
        consequence: `Repair charged to credit: ${fmt(r.amount)} plus interest until paid off.`,
        signal: 'Credit turns a one-time repair into a long, expensive tail. Use it only when cash truly is not available.',
        impact: -Math.round(r.amount * 1.18),
        score: -5,
      },
      postpone: {
        consequence: `Repair postponed. Reliability and resale value take a hit; problem usually grows.`,
        signal: 'Postponed car repairs almost always cost more later — and can become a safety issue.',
        impact: -Math.round(r.amount * 1.4),
        score: -7,
      },
    };
    const meta = consequenceByChoice[choice];
    recordDecision({
      key: 'repair',
      label: 'Surprise repair',
      choice: choice === 'pay_cash' ? 'Pay from savings'
        : choice === 'use_credit' ? 'Put on credit card'
        : 'Postpone the repair',
      consequence: meta.consequence,
      lumiSignal: meta.signal,
      walletImpact: meta.impact,
      scoreDelta: meta.score,
    });
  }, [recordDecision]);

  /* Build the recap shown on the completion screen + persisted. */
  const buildRecap = useCallback((didPurchase: boolean) => {
    const log = ref.current.decisionLog;
    const totalWallet = log.reduce((s, e) => s + e.walletImpact, 0);
    const totalScore = log.reduce((s, e) => s + e.scoreDelta, 0);
    const strongest = [...log].sort((a, b) => b.scoreDelta - a.scoreDelta)[0] || null;
    const weakest = [...log].sort((a, b) => a.scoreDelta - b.scoreDelta)[0] || null;

    let readiness: ReadinessLabel = 'Risk Watcher';
    if (!didPurchase) readiness = 'Patient Walker';
    else if (totalScore >= 22) readiness = 'Cashflow Strategist';
    else if (totalScore >= 12) readiness = 'Smart Protector';
    else if (totalScore >= 0) readiness = 'Risk Watcher';
    else if (ref.current.selectedVehicleKey === 'luxury') readiness = 'Status Chaser';
    else readiness = 'Overextended Buyer';

    const tradeoff = (() => {
      const dpEntry = log.find((e) => e.key === 'down_payment');
      const termEntry = log.find((e) => e.key === 'loan_term');
      if (termEntry && termEntry.scoreDelta < 0) return 'You chose a low monthly payment but paid for it in long-term interest.';
      if (dpEntry && dpEntry.scoreDelta < 0) return 'A small down payment kept cash on hand but left you owing most of the car.';
      const repairEntry = log.find((e) => e.key === 'repair');
      if (repairEntry && repairEntry.scoreDelta < 0) return 'You handled the repair in the most expensive way — surprise costs are where ownership math actually lives.';
      return 'You balanced upfront cost, monthly payment, and reserves — the three knobs every buyer adjusts.';
    })();

    const takeaway = didPurchase
      ? 'Affordability is not "Can I make the monthly?" — it is "Can I make the monthly AND still cover a surprise repair?"'
      : 'Walking away is a real decision. Most expensive car mistakes are the ones bought because the buyer was already standing on the lot.';

    const nextAction = didPurchase
      ? 'Open Risk Protection next: insurance and emergency cushion decide whether a single repair becomes a financial event.'
      : 'Try the Budget Builder module to see exactly how much room your real monthly cash flow has.';

    return { totalWallet, totalScore, readiness, strongest, weakest, tradeoff, takeaway, nextAction };
  }, []);

  const startSim = useCallback(async () => {
    let newProfile = DEFAULT_PROFILE;
    let type: 'live' | 'default' = 'default';
    if (accounts.length > 0) {
      const ch = accounts.find(a => a.account_type === 'checking');
      const sv = accounts.find(a => a.account_type === 'savings');
      const em = accounts.find(a => a.account_type === 'emergency_fund');
      if (ch && ch.balance > 0) {
        type = 'live';
        newProfile = {
          checking: ch.balance, savings: sv?.balance || 0,
          emergencyFund: em?.balance || 0,
          monthlyIncome: DEFAULT_PROFILE.monthlyIncome,
          monthlyExpenses: DEFAULT_PROFILE.monthlyExpenses,
          creditScore: DEFAULT_PROFILE.creditScore,
          existingDebt: 0,
        };
      }
    }
    setScenarioType(type);
    setProfile(newProfile);
    setScreenRaw('marketplace');
    if (!simId && user) {
      const { data } = await supabase.from("vehicle_simulations").insert({
        user_id: user.id, current_screen: 'marketplace',
        scenario_type: type === 'live' ? 'live_user_finances' : 'default_profile',
        financial_profile: newProfile as AnyValue, credit_score: newProfile.creditScore,
      }).select().single();
      if (data) setSimId((data).id);
    } else {
      persist('marketplace');
    }
  }, [accounts, user, simId, persist]);

  const attemptNegotiation = useCallback(() => {
    if (!selectedVehicle) return 0;
    const maxDiscount = Math.round(selectedVehicle.purchase_price * 0.05);
    const roll = Math.random();
    let discount = 0;
    if (roll > 0.6) discount = Math.round(maxDiscount * (0.4 + Math.random() * 0.6));
    else if (roll > 0.3) discount = Math.round(maxDiscount * 0.2 * Math.random());
    setNegotiationDiscount(discount);
    return discount;
  }, [selectedVehicle]);

  const completePurchase = useCallback(async () => {
    if (!user) {
      toast.error("You must be logged in to complete a purchase.");
      return;
    }
    if (!selectedVehicle) {
      toast.error("No vehicle selected. Please go back and choose a vehicle.");
      return;
    }
    if (!simId) {
      toast.error("Simulation not initialized. Please restart the module.");
      return;
    }
    if (!purchaseReason) {
      toast.error("Please select a purchase reason before buying.");
      return;
    }

    // Validate affordability
    if (liquidAfter < 0) {
      toast.error("You don't have enough liquid funds to cover the upfront costs.");
      return;
    }

    setProcessing(true);
    try {
      if (scenarioType === 'live') {
        const { error } = await supabase.rpc("deduct_purchase_funds", { p_amount: upfrontCash });
        if (error) {
          toast.error(`Purchase failed: ${error.message}`);
          setProcessing(false);
          return;
        }
      }

      const { error: assetError } = await supabase.from("user_assets").insert({
        user_id: user.id, asset_type: 'vehicle', asset_name: selectedVehicle.vehicle_name,
        purchase_price: effectivePrice, estimated_value: Math.round(effectivePrice * 0.85),
        starting_equity: downPaymentAmount, linked_simulation_id: simId,
      });
      if (assetError) {
        toast.error(`Failed to record asset: ${assetError.message}`);
        setProcessing(false);
        return;
      }

      if (loanAmount > 0) {
        await supabase.from("user_liabilities").insert({
          user_id: user.id, liability_type: 'auto_loan', liability_name: `${selectedVehicle.vehicle_name} Loan`,
          original_balance: loanAmount, current_balance: loanAmount, monthly_payment_estimate: monthlyPayment,
          linked_simulation_id: simId,
        });
      }

      // Add monthly expenses for vehicle costs
      const expenseEntries = [
        { category: 'transportation', description: `${selectedVehicle.vehicle_name} - Loan Payment`, amount: monthlyPayment, source_module: 'vehicle_ownership' },
        { category: 'insurance', description: `${selectedVehicle.vehicle_name} - Insurance`, amount: selectedVehicle.insurance_monthly, source_module: 'vehicle_ownership' },
        { category: 'transportation', description: `${selectedVehicle.vehicle_name} - Fuel`, amount: selectedVehicle.fuel_monthly, source_module: 'vehicle_ownership' },
        { category: 'transportation', description: `${selectedVehicle.vehicle_name} - Maintenance`, amount: selectedVehicle.maintenance_monthly, source_module: 'vehicle_ownership' },
      ].filter(e => e.amount > 0);

      for (const exp of expenseEntries) {
        await supabase.from("monthly_expenses").insert({
          user_id: user.id, ...exp, linked_simulation_id: simId,
        });
      }

      // Module cash comes from the server catalogue ($30 simulator tier) and is
      // claimable exactly once per learner.
      try {
        await supabase.rpc("record_module_completion" as never, {
          p_module_key: "vehicle_ownership", p_score: 1, p_max_score: 1,
        } as never);
        await supabase.rpc("award_module_completion_reward" as never, {
          p_module_key: "vehicle_ownership",
        } as never);
      } catch { /* non-critical: failure is safe to ignore here */ }

      // Credit charge
      try {
        await supabase.rpc("auto_credit_charge", {
          p_amount: 25 + Math.floor(Math.random() * 26),
          p_description: 'Credit Purchase: Vehicle Accessories & Registration Supplies',
        });
      } catch { /* non-critical: failure is safe to ignore here */ }

      const achievements: string[] = [];
      if (affordabilityRating === 'strong') achievements.push('smart_buyer');
      if (liquidAfter >= profile.emergencyFund * 0.8) achievements.push('cash_cushion_protector');
      if (negotiationDiscount > 0) achievements.push('negotiator');
      if (!reasonMismatch) achievements.push('needs_first');
      if (loanTermMonths <= 48) achievements.push('long_term_thinker');
      if (affordabilityRating === 'strong' && !reasonMismatch) achievements.push('balanced_decision');
      if (selectedVehicleKey === 'luxury' && (affordabilityRating === 'overextended' || affordabilityRating === 'unhealthy')) achievements.push('status_chaser');
      if (selectedVehicleKey === 'utility' && (purchaseReason === 'work')) achievements.push('tool_for_growth');

      const recap = buildRecap(true);

      setPurchaseCompleted(true);
      setScreenRaw('completed');
      await persist('completed', {
        final_decision: 'purchased', purchase_completed: true,
        purchase_completed_at: new Date().toISOString(), status: 'completed',
        affordability_rating: affordabilityRating, purchase_grade: purchaseGrade,
        achievements,
        negotiation_result: {
          discount: ref.current.negotiationDiscount,
          decision_log: ref.current.decisionLog,
          repair: ref.current.repair,
          recap,
        },
      });

      toast.success(`🚗 ${selectedVehicle.vehicle_name} purchased successfully!`);

      if (scenarioType === 'live') await refetchAccounts();
    } catch (err: unknown) {
      toast.error(`Purchase error: ${getErrorMessage(err) || 'Unknown error'}`);
    } finally {
      setProcessing(false);
    }
  }, [user, selectedVehicle, simId, scenarioType, upfrontCash, effectivePrice, downPaymentAmount, loanAmount, monthlyPayment, affordabilityRating, purchaseGrade, liquidAfter, profile, negotiationDiscount, reasonMismatch, selectedVehicleKey, purchaseReason, loanTermMonths, persist, refetchAccounts, buildRecap]);

  const exitWithoutPurchase = useCallback(async () => {
    if (!simId) return;
    // Walking away is itself a meaningful decision.
    recordDecision({
      key: 'final',
      label: 'Final decision',
      choice: 'Walked away from the purchase',
      consequence: 'No payment, no loan, no insurance increase. Cash position unchanged.',
      lumiSignal: 'Saying no is free. Saying yes to the wrong car is the most expensive choice on this screen.',
      walletImpact: 0,
      scoreDelta: 10,
    });
    const recap = buildRecap(false);
    setScreenRaw('completed');
    setPurchaseCompleted(false);
    await persist('completed', {
      final_decision: 'walked_away',
      status: 'completed',
      negotiation_result: {
        discount: ref.current.negotiationDiscount,
        decision_log: ref.current.decisionLog,
        repair: ref.current.repair,
        recap,
      },
    });
  }, [simId, persist, recordDecision, buildRecap]);

  const tryAgain = useCallback(async () => {
    setSelectedVehicleKey(null);
    setPurchaseReason(null);
    setDownPaymentPct(0.1);
    setLoanTermMonths(60);
    setNegotiationDiscount(0);
    setPurchaseCompleted(false);
    setDecisionLog([]);
    setCommittedSteps({});
    setRepair({ amount: 0, description: '', choice: null });
    setScreenRaw('marketplace');
    if (simId) {
      await supabase.from("vehicle_simulations").update({
        current_screen: 'marketplace', status: 'in_progress',
        selected_vehicle_key: null, purchase_reason: null, final_decision: null,
        purchase_completed: false, negotiation_result: '{}',
        updated_at: new Date().toISOString(),
      }).eq("id", simId);
    }
  }, [simId]);

  /* Generate the repair event lazily the first time the user lands on  */
  /* the repair screen so the description stays stable across re-renders.*/
  const ensureRepairEvent = useCallback(() => {
    if (!repair.amount && selectedVehicle) {
      setRepair(buildRepairEvent(selectedVehicle));
    }
  }, [repair.amount, selectedVehicle]);

  return {
    screen, goTo, loading, processing, vehicles, profile, selectedVehicle, selectedVehicleKey,
    setSelectedVehicleKey, purchaseReason, setPurchaseReason, downPaymentPct, setDownPaymentPct,
    loanTermMonths, setLoanTermMonths, negotiationDiscount, effectivePrice,
    downPaymentAmount, loanAmount, apr, monthlyPayment, totalPaid, totalInterest,
    hiddenCosts, upfrontCash, liquidBefore, liquidAfter, totalMonthlyCost,
    monthlyRemaining, dti, affordabilityRating, reasonMismatch, purchaseGrade,
    firstYearCost, progress, startSim, completePurchase, exitWithoutPurchase,
    purchaseCompleted, popupKey, setPopupKey, scenarioType, tryAgain,
    attemptNegotiation, aprMap,
    decisionLog, committedSteps, repair, ensureRepairEvent,
    commitVehicleChoice, commitDownPayment, commitLoanTerm, commitNegotiation, commitRepair,
    buildRecap,
  };
}
